/** Pure tile stylesheet constructors and the eight unchanged family palettes. */
import { TileType, getTileName } from '../../../shared/TileEditorTypes';
import type { BiomeTileStyle, TileCategory, BiomeTextureProfile, BiomeTileFamily } from './BiomeTextureModule';

const tileKey = (tile: TileType, scope: string): string => `tile_${getTileName(tile).toLowerCase()}_${scope}`;

/** Build a tile style bound to a family/biome scope. */
export const tile = (t: TileType, scope: string, tint: number): BiomeTileStyle => ({ textureKey: tileKey(t, scope), tint });

/** Build the 3 category defaults from ground/collision/background tints. */
export const cats = (scope: string, ground: number, collision: number, background: number): Record<TileCategory, BiomeTileStyle> => ({
  ground: { textureKey: `tile_ground_${scope}`, tint: ground },
  collision: { textureKey: `tile_collision_${scope}`, tint: collision },
  background: { textureKey: `bg_${scope}`, tint: background },
});

// ─── family base profiles ───────────────────────────────────────────────────
export const FAMILY_PROFILES: Record<BiomeTileFamily, BiomeTextureProfile> = {
  cold: {
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'cold', 0x6b7e8c),
      [TileType.GRASS]: tile(TileType.GRASS, 'cold', 0x8aa68b),
      [TileType.WALL]: tile(TileType.WALL, 'cold', 0x4a6076),
      [TileType.WATER]: tile(TileType.WATER, 'cold', 0x2a5a7a),
      [TileType.ICE]: tile(TileType.ICE, 'cold', 0xbfe9ff),
      [TileType.STONE]: tile(TileType.STONE, 'cold', 0x556677),
      [TileType.WOOD]: tile(TileType.WOOD, 'cold', 0x6a5a4a),
    },
    categories: cats('cold', 0x6b7e8c, 0x4a6076, 0x44637e),
  },
  hot: {
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'hot', 0x3a2418),
      [TileType.GRASS]: tile(TileType.GRASS, 'hot', 0x6a4a2a),
      [TileType.WALL]: tile(TileType.WALL, 'hot', 0x2a1410),
      [TileType.LAVA]: tile(TileType.LAVA, 'hot', 0xff3814),
      [TileType.WATER]: tile(TileType.WATER, 'hot', 0x552211),
      [TileType.STONE]: tile(TileType.STONE, 'hot', 0x4a2a1a),
      [TileType.WOOD]: tile(TileType.WOOD, 'hot', 0x4a2a1a),
    },
    categories: cats('hot', 0x3a2418, 0x2a1410, 0x6e2410),
  },
  toxic: {
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'toxic', 0x1d2a1c),
      [TileType.GRASS]: tile(TileType.GRASS, 'toxic', 0x4a8f3e),
      [TileType.WALL]: tile(TileType.WALL, 'toxic', 0x1d0b30),
      [TileType.LAVA]: tile(TileType.LAVA, 'toxic', 0x3e9e3e),
      [TileType.WATER]: tile(TileType.WATER, 'toxic', 0x1a3322),
      [TileType.STONE]: tile(TileType.STONE, 'toxic', 0x2a1a2a),
      [TileType.WOOD]: tile(TileType.WOOD, 'toxic', 0x2a1a1a),
    },
    categories: cats('toxic', 0x1d2a1c, 0x1d0b30, 0x2e0f3e),
  },
  temperate: {
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'temperate', 0x5a4a3a),
      [TileType.GRASS]: tile(TileType.GRASS, 'temperate', 0x58a83a),
      [TileType.WALL]: tile(TileType.WALL, 'temperate', 0x3a4a2a),
      [TileType.LAVA]: tile(TileType.LAVA, 'temperate', 0x6a3a1a),
      [TileType.WATER]: tile(TileType.WATER, 'temperate', 0x2a4a6a),
      [TileType.ICE]: tile(TileType.ICE, 'temperate', 0xbfe9ff),
      [TileType.STONE]: tile(TileType.STONE, 'temperate', 0x6a6a6a),
      [TileType.WOOD]: tile(TileType.WOOD, 'temperate', 0x8a5a2a),
    },
    categories: cats('temperate', 0x5a4a3a, 0x3a4a2a, 0x3a5230),
  },
  dark: {
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'dark', 0x2a2a2e),
      [TileType.GRASS]: tile(TileType.GRASS, 'dark', 0x2a3a2a),
      [TileType.WALL]: tile(TileType.WALL, 'dark', 0x1a1a1e),
      [TileType.WATER]: tile(TileType.WATER, 'dark', 0x1a2a3a),
      [TileType.ICE]: tile(TileType.ICE, 'dark', 0x8a9aaa),
      [TileType.STONE]: tile(TileType.STONE, 'dark', 0x3a3a3e),
      [TileType.WOOD]: tile(TileType.WOOD, 'dark', 0x3a2a1a),
    },
    categories: cats('dark', 0x2a2a2e, 0x1a1a1e, 0x141c28),
  },
  aquatic: {
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'aquatic', 0x0e3e46),
      [TileType.GRASS]: tile(TileType.GRASS, 'aquatic', 0x2a8a6a),
      [TileType.WALL]: tile(TileType.WALL, 'aquatic', 0x08272e),
      [TileType.WATER]: tile(TileType.WATER, 'aquatic', 0x1fb6a6),
      [TileType.ICE]: tile(TileType.ICE, 'aquatic', 0xbfe9ff),
      [TileType.STONE]: tile(TileType.STONE, 'aquatic', 0x166068),
      [TileType.WOOD]: tile(TileType.WOOD, 'aquatic', 0x4a3a2a),
    },
    categories: cats('aquatic', 0x0e3e46, 0x08272e, 0x05161c),
  },
  bright: {
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'bright', 0x8a7a6a),
      [TileType.GRASS]: tile(TileType.GRASS, 'bright', 0x7ec77a),
      [TileType.WALL]: tile(TileType.WALL, 'bright', 0x6a5a4a),
      [TileType.LAVA]: tile(TileType.LAVA, 'bright', 0xff8a3c),
      [TileType.WATER]: tile(TileType.WATER, 'bright', 0x4a9acf),
      [TileType.ICE]: tile(TileType.ICE, 'bright', 0xdcefff),
      [TileType.STONE]: tile(TileType.STONE, 'bright', 0x9a9a9a),
      [TileType.WOOD]: tile(TileType.WOOD, 'bright', 0xb08a44),
    },
    categories: cats('bright', 0x8a7a6a, 0x6a5a4a, 0xffe2a0),
  },
  ethereal: {
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'ethereal', 0x2a1a4a),
      [TileType.GRASS]: tile(TileType.GRASS, 'ethereal', 0x4a3a8a),
      [TileType.WALL]: tile(TileType.WALL, 'ethereal', 0x241848),
      [TileType.WATER]: tile(TileType.WATER, 'ethereal', 0x2a4a8a),
      [TileType.STONE]: tile(TileType.STONE, 'ethereal', 0x3a2a5a),
      [TileType.WOOD]: tile(TileType.WOOD, 'ethereal', 0x4a3a6a),
    },
    categories: cats('ethereal', 0x2a1a4a, 0x241848, 0x241848),
  },
};

/** Woodland harbor surfaces match the town materials; the backdrop matches its sky horizon. */
export const WICKHAVEN_TILE_PROFILE: BiomeTextureProfile = {
  tiles: {
    [TileType.FLOOR]: tile(TileType.FLOOR, 'wickhaven', 0x465651),
    [TileType.GRASS]: tile(TileType.GRASS, 'wickhaven', 0x728569),
    [TileType.WALL]: tile(TileType.WALL, 'wickhaven', 0x273b3b),
    [TileType.WATER]: tile(TileType.WATER, 'wickhaven', 0x36595c),
    [TileType.STONE]: tile(TileType.STONE, 'wickhaven', 0x53675f),
    [TileType.WOOD]: tile(TileType.WOOD, 'wickhaven', 0x65503f),
  },
  categories: cats('wickhaven', 0x465651, 0x273b3b, 0x667d79),
};
